import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "store/store";
import { RootState } from "store/reducers";
import { Link } from "react-router-dom";
import { Link as SwitcherLink } from "components/buttons/Link";
import { client } from "api/client";
import {
    Broadcast,
    ProjectBroadcastDefault,
    BroadcastStatus
} from "@switcherstudio/switcher-api-client";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import styles from "./SimulcastExistingBroadcast.module.scss";
import { SimulcastExistingTable } from "./SimulcastExistingTable";
import rollbar from "helpers/rollbar";
import dayjs from "dayjs";
import { useSimulcastFork } from "hooks/useSimulcastFork";
import { Trans } from "react-i18next";
import { usePageHeader } from "hooks/usePageHeader";
import { useSwitcherSdk } from "hooks/useSwitcherSdk";
import { resetGeneralForm } from "store/platforms/slice";
import { closeCurrentModal, setActiveModal } from "store/modal/slice";
import { Modals } from "store/modal/types";
import { AttentionModal } from "components/modal/AttentionModal";

export const SimulcastExistingBroadcasts: React.FC = () => {
    // display existing streams
    // show currently selected stream - if any.
    // user should be able to click an existing stream to 'set as default'
    const user = useSelector((state: RootState) => state.user);
    const { simulcastType, t } = useSimulcastFork();
    const dispatch = useDispatch<AppDispatch>();
    const { isInBrowser } = useSwitcherSdk();
    const [scheduledBroacasts, setScheduledBroadcasts] = useState<Broadcast[]>(
        []
    );
    const [liveNowBroadcasts, setLiveNowBroadcasts] = useState<Broadcast[]>([]);
    const [defaultBroadcast, setDefaultBroadcast] =
        useState<ProjectBroadcastDefault>();
    const projectId = user?.userInfo?.MetaData?.Details?.ProjectId;

    const parseStreams = (_existingStreams) => {
        const scheduledBcasts = [];
        const liveNowBcasts = [];

        _existingStreams.forEach((s) => {
            if (dayjs(s.StartsAt).isAfter(dayjs().subtract(30, "minute"))) {
                scheduledBcasts.push(s);
            } else if (s.StartsAt === null) {
                liveNowBcasts.push(s);
            }
        });

        return {
            scheduledBcasts,
            liveNowBcasts
        };
    };

    const getBroadcastInfo = useCallback(async () => {
        if (!projectId) return;
        try {
            const _existingStreams =
                await client.projects_GetProjectBroadcastsByFilter(
                    projectId,
                    "simulcast",
                    BroadcastStatus._1
                );
            const _defaultBroadcast = await client.projects_GetDefaultBroadcast(
                projectId
            );
            const { scheduledBcasts, liveNowBcasts } =
                parseStreams(_existingStreams);

            setScheduledBroadcasts(scheduledBcasts);
            setLiveNowBroadcasts(liveNowBcasts);
            setDefaultBroadcast(_defaultBroadcast);
        } catch (err) {
            rollbar.error("Error getting simulcast broadcast info", err);
            dispatch(
                addNotification({
                    type: NotificationType.Danger,
                    message: err?.message
                })
            );
        }
    }, [dispatch, projectId]);

    useEffect(() => {
        getBroadcastInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    usePageHeader({
        subTitle: isInBrowser ? (
            <Trans
                i18nKey={`${simulcastType}:scheduled-events-cta`}
                components={{
                    link1: (
                        <SwitcherLink
                            to={
                                "https://itunes.apple.com/us/app/switcher-studio-pro/id908386221"
                            }
                            target="_blank"
                        />
                    )
                }}
            />
        ) : (
            <></>
        )
    });

    const setNewDefault = async (bcastId) => {
        await client.projects_PostDefaultBroadcast(projectId, bcastId);
        const _defaultBroadcast = await client.projects_GetDefaultBroadcast(
            projectId
        );
        setDefaultBroadcast(_defaultBroadcast);

        dispatch(
            addNotification({
                type: NotificationType.Success,
                message: `${simulcastType}:default-broadcast-selected`
            })
        );
    };

    const triggerDelete = useCallback(
        async (broadcastId) => {
            try {
                await client.broadcasts_DeleteBroadcast(broadcastId);

                getBroadcastInfo();

                dispatch(
                    addNotification({
                        type: NotificationType.Success,
                        message: `messages:delete-${simulcastType}-success`
                    })
                );
            } catch (err) {
                rollbar.error("Error deleting multistream", err);
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: `errors:delete-${simulcastType}-error`,
                        messageOptions: { rawError: err?.message }
                    })
                );
            }
        },
        [dispatch, getBroadcastInfo, simulcastType]
    );

    const closeAttentionModal = useCallback(() => {
        dispatch(closeCurrentModal());
    }, [dispatch]);

    const confirmAndDelete = useCallback(
        async (broadcastId) => {
            await dispatch(
                setActiveModal({
                    id: Modals.AttentionModal,
                    type: Modals.AttentionModal,
                    component: (
                        <AttentionModal
                            isOpen={true}
                            setIsOpen={closeAttentionModal}
                            handleCancel={closeAttentionModal}
                            hasContinueButton={true}
                            handleContinue={() => triggerDelete(broadcastId)}
                            continueText={t("buttons:confirm")}
                        >
                            <div>
                                {t(
                                    `messages:delete-${simulcastType}-confirmation`
                                )}
                            </div>
                        </AttentionModal>
                    )
                })
            );
        },
        [closeAttentionModal, dispatch, t, triggerDelete, simulcastType]
    );

    return (
        <div className={styles["container"]}>
            <Link
                className={`btn btn-primary ${styles["create-simulcast-btn"]}`}
                to="/platforms/simulcast/destinations"
                onClick={() => dispatch(resetGeneralForm())}
            >
                {t("create-new")}
            </Link>
            <div>
                <h2>{t("existing-streams")}</h2>
                {!!liveNowBroadcasts?.length || !!scheduledBroacasts?.length ? (
                    <p>{t("default-stream-select")}</p>
                ) : (
                    <p>{t("empty-streams")}</p>
                )}

                {/* LIVE NOW EVENTS */}

                {!!liveNowBroadcasts?.length && (
                    <>
                        <h4>{t("live-now-streams")}</h4>
                        <SimulcastExistingTable
                            existingStreams={liveNowBroadcasts}
                            defaultBroadcastId={defaultBroadcast?.BroadcastId}
                            onChange={setNewDefault}
                            triggerDelete={confirmAndDelete}
                        />
                    </>
                )}

                {/* UPCOMING EVENTS */}
                {!!scheduledBroacasts?.length && (
                    <>
                        <h4>{t("scheduled-streams")}</h4>
                        <SimulcastExistingTable
                            existingStreams={scheduledBroacasts}
                            defaultBroadcastId={defaultBroadcast?.BroadcastId}
                            onChange={setNewDefault}
                            triggerDelete={confirmAndDelete}
                        />
                    </>
                )}
            </div>
        </div>
    );
};
